import React, { useState, useEffect, useMemo } from 'react';
import { BellIcon } from '@heroicons/react/24/outline';
import { useNotifications } from '../../hooks/useNotifications';
import { useWebSocket } from '../../hooks/useWebSocket';
import { notificationsAPI } from '../../services/api';
const NotificationBell = () => {
  const { isPermissionGranted } = useNotifications();
  const { isConnected, subscribe, unsubscribe, listen } = useWebSocket();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  // Get current user ID
  const currentUserId = useMemo(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('user_data') || '{}');
      return userData.user_id || userData.id;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  }, []);

  // Mock notifications - replace with actual API calls
  useEffect(() => {
    // Fetch notifications from your API
    const fetchNotifications = async () => {
      try {
        const response = await notificationsAPI.getNotificationsWithRead('drawer');
        const result = response.data;
        const notificationsArray = result?.data || result || [];
        setNotifications(notificationsArray);
        setUnreadCount(notificationsArray.length);
      } catch (error) {
        console.error('Error fetching notifications:', error);
        // Mock data for development
        setNotifications([
          {
            id: 1,
            title: 'New Chat Message',
            message: 'You have a new message from Dr. Smith',
            type: 'chat_message',
            timestamp: new Date().toISOString(),
            isRead: false
          },
          {
            id: 2,
            title: 'Appointment Reminder',
            message: 'Your pet\'s appointment is tomorrow at 2 PM',
            type: 'appointment_reminder',
            timestamp: new Date(Date.now() - 3600000).toISOString(),
            isRead: false
          }
        ]);
        setUnreadCount(2);
      }
    };

    if (isPermissionGranted) {
      fetchNotifications();
    }
  }, [isPermissionGranted]);

  // Subscribe to real-time notifications via WebSocket
  useEffect(() => {
    if (!isConnected || !currentUserId) {
      return;
    }

    const channelName = `App.Models.User.${currentUserId}`;
    
    try {
      // Subscribe to user's private notification channel
      const channel = subscribe(channelName, null);
      
      if (channel) {
        // Listen for notification events (you may need to create a NotificationCreated event on backend)
        // For now, we'll listen for any events that might indicate new notifications
        listen(channelName, 'notification.created', (data) => {
          
          // Add new notification to the list
          const newNotification = {
            id: data.id || Date.now(),
            title: data.title || data.message || 'New Notification',
            message: data.message || data.body || '',
            type: data.type || 'general',
            timestamp: data.created_at || new Date().toISOString(),
            isRead: false,
          };

          setNotifications(prev => [newNotification, ...prev]);
          setUnreadCount(prev => prev + 1);
        });
      }
    } catch (error) {
      console.error('Error subscribing to notification channel:', error);
    }

    // Cleanup
    return () => {
      if (currentUserId) {
        unsubscribe(`App.Models.User.${currentUserId}`);
      }
    };
  }, [isConnected, currentUserId, subscribe, unsubscribe, listen]);

  const markAsRead = async (notificationId) => {
    try {
      const response = await fetch(`/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setNotifications(prev => 
          prev.map(notif => 
            notif.id === notificationId 
              ? { ...notif, isRead: true }
              : notif
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await fetch('/api/notifications/mark-all-read', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setNotifications(prev => 
          prev.map(notif => ({ ...notif, isRead: true }))
        );
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'chat_message':
        return '💬';
      case 'appointment_reminder':
        return '📅';
      case 'pet_health_update':
        return '🏥';
      // case 'marketplace_update': // Commented out for now - will be used later
      //   return '🛍️';
      case 'social_update':
        return '👥';
      default:
        return '🔔';
    }
  };

  if (!isPermissionGranted) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
      >
        <BellIcon className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Mark all as read
                </button>
              )}
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                <p>No notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      !notification.isRead ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => markAsRead(notification.id)}
                  >
                    <div className="flex items-start space-x-3">
                      <span className="text-lg">{getNotificationIcon(notification.type)}</span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${
                          !notification.isRead ? 'text-gray-900' : 'text-gray-700'
                        }`}>
                          {notification.title}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                          {formatTimestamp(notification.timestamp)}
                        </p>
                      </div>
                      {!notification.isRead && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => {
                  // Navigate to notifications page
                  window.location.href = '/notifications';
                }}
                className="w-full text-center text-sm text-blue-600 hover:text-blue-800"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default NotificationBell;
